import './PackagesListFilter.scss';

import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useForm } from '@refinedev/antd';
import { useResourceParams } from '@refinedev/core';
import { Button, Checkbox, Form, Input, Popover } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import FullscreenDrawer from '@/components/general/Drawers/FullscreenDrawer';
import { useUiContext } from '@/contexts/UiContext';
import { CompanyTypesString } from '@/model/Logistic/CompanyTypes';
import {
  PackageStateForReceptionsString,
  PackageStateString,
} from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { RESOURCES } from '@/Resources';

import { usePackagesListFilter } from './usePackagesListFilter';

interface PackagesListFilterProps {
  filters: any[];
  setFilters: Function;
  setCurrent: Function;
}

const PackagesListFilter = (props: PackagesListFilterProps) => {
  const { t } = useTranslation();
  const { resource } = useResourceParams();
  const { isMobile } = useUiContext();
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const { formProps } = useForm<Shipment>({ warnWhenUnsavedChanges: false });
  const { form } = formProps;
  const { filters, setFilters, setCurrent } = props;
  const { usersSelectProps, companySelectProps, shippersSelectProps, ticketsSelectProps } =
    usePackagesListFilter(formProps, filters);
  const isShipmentList = useMemo(() => resource?.name === RESOURCES.SHIPMENTS, [resource]);
  const isReceptionList = useMemo(() => resource?.name === RESOURCES.RECEPTIONS, [resource]);
  const handleClearFilters = useCallback(() => {
    form?.resetFields();
    setFilters([], 'replace');
    setCurrent(1);
    setFilterPopoverOpen(false);
  }, [form, setCurrent, setFilters]);
  const applyFilters = useCallback(
    values => {
      const filters: any[] = [];

      // Empresa
      if (values.company) {
        filters.push({
          field: 'company',
          operator: 'eq',
          value: values.company,
        });
      }

      // Tipo de empresa
      if (values.company_type) {
        filters.push({
          field: 'company_type',
          operator: 'eq',
          value: values.company_type,
        });
      }

      // Razon social
      if (values.shipment_payment_company) {
        filters.push({
          field: 'shipment_payment_company',
          operator: 'eq',
          value: values.shipment_payment_company,
        });
      }

      // Operador logístico
      if (values.shipper) {
        filters.push({
          field: 'shipper',
          operator: 'eq',
          value: values.shipper,
        });
      }

      // Estado
      if (values.status != undefined && values.status.length > 0) {
        filters.push({
          field: 'status',
          operator: 'eq',
          value: values.status.join(','),
        });
      }

      // Creado por
      if (values.created_by) {
        filters.push({
          field: 'created_by',
          operator: 'eq',
          value: values.created_by,
        });
      }

      // Ticket
      if (values.ticket) {
        filters.push({
          field: 'ticket',
          operator: 'eq',
          value: values.ticket,
        });
      }

      // Ticket
      if (values.tipo != undefined) {
        filters.push({
          field: 'tipo',
          operator: 'eq',
          value: values.tipo,
        });
      }

      // Numero de serie incluido
      if (values.device_serial != undefined) {
        filters.push({
          field: 'device_serial',
          operator: 'eq',
          value: values.device_serial,
        });
      }

      // Numero de serie incluido
      if (values.active_claim) {
        filters.push({
          field: 'active_claim',
          operator: 'eq',
          value: values.active_claim,
        });
      }

      setFilters(filters, 'replace');
      setCurrent(1);
      setFilterPopoverOpen(false);
    },
    [setFilters, setFilterPopoverOpen, setCurrent],
  );
  const filterContent = useMemo(
    () => (
      <Form
        labelAlign="left"
        {...formProps}
        style={{ marginBottom: 8 }}
        onFinish={applyFilters}
        className="PackagesListFilterPopover">
        <div className="PackagesListFilterPopoverRows">
          <Form.Item label={t('Ticket')} name={'ticket'} className="PackagesListFilterRow">
            <AntdSelect {...ticketsSelectProps} allowClear showSearch />
          </Form.Item>
          <Form.Item label={t('Empresa')} name={'company'} className="PackagesListFilterRow">
            {/* <CompanySelector showFullName {...companySelectProps} allowClear showSearch /> */}
            <AntdSelect {...companySelectProps} allowClear showSearch />
          </Form.Item>
          <Form.Item
            label={t('Tipo de empresa')}
            name={'company_type'}
            className="PackagesListFilterRow">
            <AntdSelect
              options={Object.keys(CompanyTypesString).map(v => ({
                label: CompanyTypesString[parseInt(v)],
                value: parseInt(v),
              }))}
              allowClear
            />
          </Form.Item>
          {isShipmentList && (
            <Form.Item
              label={t('Razon social')}
              name={'shipment_payment_company'}
              className="PackagesListFilterRow">
              {/* <CompanySelector showFullName {...companySelectProps} allowClear showSearch /> */}
              <AntdSelect {...companySelectProps} allowClear showSearch />
            </Form.Item>
          )}

          <Form.Item
            label={t('Operador logístico')}
            name={'shipper'}
            className="PackagesListFilterRow">
            <AntdSelect {...shippersSelectProps} allowClear showSearch />
          </Form.Item>

          <Form.Item label={t('Estado')} name={'status'} className="PackagesListFilterRow">
            <AntdSelect
              mode="multiple"
              options={Object.keys(
                isShipmentList ? PackageStateString : PackageStateForReceptionsString,
              ).map(v => ({
                label: PackageStateString[parseInt(v)],
                value: parseInt(v),
              }))}
              allowClear
            />
          </Form.Item>
          <Form.Item label={t('Creado por')} name={'created_by'} className="PackagesListFilterRow">
            <AntdSelect options={usersSelectProps.options} allowClear showSearch />
          </Form.Item>
          <Form.Item label={t('Tipo')} name={'tipo'} className="PackagesListFilterRow">
            <AntdSelect
              options={[
                { label: t('Manual'), value: 0 },
                { label: t('Automático'), value: 1 },
              ]}
              allowClear
            />
          </Form.Item>
          <Form.Item
            label={t('Numero de serie')}
            tooltip={t('Filtra los paquetes que incluyan el número de serie indicado')}
            name={'device_serial'}
            className="PackagesListFilterRow">
            <Input placeholder={t('Número de serie')} />
          </Form.Item>

          {isReceptionList && (
            <Form.Item
              label={t('Reclamables')}
              tooltip={t('Filtra los paquetes que sean reclamables')}
              name={'active_claim'}
              className="PackagesListFilterRow"
              valuePropName="checked">
              <Checkbox />
            </Form.Item>
          )}
        </div>

        <div className="PackagesListFilterActions">
          <Button type="default" style={{ marginLeft: 8 }} onClick={() => handleClearFilters()}>
            {t('Limpiar')}
          </Button>
          <Button htmlType="submit" type="primary">
            {t('Buscar')}
          </Button>
        </div>
      </Form>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companySelectProps, usersSelectProps],
  );

  return (
    <div className="PackagesListFilter">
      {isMobile ? (
        <>
          <Button
            className={filters.length > 0 ? 'PackagesListFilterApplied' : ''}
            onClick={() => setFilterPopoverOpen(true)}
            icon={<FilterOutlined />}
          />
          <FullscreenDrawer
            disablePush
            title={t('Filtros')}
            open={filterPopoverOpen}
            setOpen={setFilterPopoverOpen}>
            {filterContent}
          </FullscreenDrawer>
        </>
      ) : (
        <Popover
          openClassName="PackagesListFilterPopover"
          content={filterContent}
          title={t('Filtros')}
          trigger="click"
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          placement="bottomRight">
          <Button
            className={filters.length > 0 ? 'PackagesListFilterApplied' : ''}
            icon={filters.length > 0 ? <FilterFilled /> : <FilterOutlined />}>
            {t('Filtrar')}
          </Button>
        </Popover>
      )}
    </div>
  );
};

export default PackagesListFilter;
